import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { downloadCSV } from "@/lib/export";
import { useLang } from "@/context/LanguageContext";
import { BarChart3, Loader2, ShieldCheck, Clock, Zap, CheckCircle2, Download } from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";

const SEV_COLORS = { low: "#6D9C55", medium: "#D98014", high: "#B84A22", critical: "#7A1F0C" };

function Stat({ icon: Icon, label, value, tone = "#275C3F", testid }) {
  return (
    <div data-testid={testid} className="bg-white border border-[#DCE3DD] rounded-sm p-5">
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.15em] font-semibold text-[#4B5950]">{label}</span>
        <Icon className="h-4 w-4" style={{ color: tone }} />
      </div>
      <p className="font-display text-3xl font-bold mt-3" style={{ color: tone }}>{value}</p>
    </div>
  );
}

export default function Reports() {
  const { t } = useLang();
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/reports?days=${days}`).then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [days]);

  const ranges = [[7, t("reports.range7")], [30, t("reports.range30")], [90, t("reports.range90")]];

  const exportReport = () => {
    if (!data) return;
    const rows = [
      { metric: t("reports.resolutionRate"), value: `${data.resolution_rate}%` },
      { metric: t("reports.avgResolution"), value: `${data.avg_resolution_hours} ${t("reports.hours")}` },
      { metric: t("reports.autoGenerated"), value: data.auto_generated },
      { metric: t("reports.completedItems"), value: `${data.compliance_completed}/${data.compliance_total}` },
      { metric: t("reports.totalIncidents"), value: data.total_incidents },
      { metric: t("reports.openIncidents"), value: data.open_incidents },
      { metric: "", value: "" },
      { metric: t("reports.severityDist"), value: "" },
      ...data.severity.map((s) => ({ metric: `  ${t(`severity.${s.name}`)}`, value: s.count })),
      { metric: "", value: "" },
      { metric: t("reports.incidentsOverTime"), value: "" },
      ...data.timeseries.map((p) => ({ metric: `  ${p.date}`, value: p.count })),
    ];
    downloadCSV(
      `envisafe-report-${days}d.csv`,
      [{ label: "Metric", key: "metric" }, { label: "Value", key: "value" }],
      rows
    );
  };

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold flex items-center gap-2"><BarChart3 className="h-7 w-7 text-[#275C3F]" /> {t("reports.title")}</h1>
          <p className="text-[#4B5950] mt-1">{t("reports.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1.5">
            {ranges.map(([d, label]) => (
              <button key={d} data-testid={`range-${d}`} onClick={() => setDays(d)} className={`text-xs font-medium px-3 py-1.5 rounded-sm border transition-all duration-200 ${days === d ? "bg-[#275C3F] text-white border-[#275C3F]" : "bg-white border-[#DCE3DD] text-[#4B5950] hover:bg-[#EAF0EA]"}`}>
                {label}
              </button>
            ))}
          </div>
          <button data-testid="export-report-button" onClick={exportReport} disabled={!data} className="inline-flex items-center gap-1.5 border border-[#DCE3DD] bg-white px-3 py-1.5 rounded-sm text-xs font-medium hover:bg-[#EAF0EA] transition-all duration-200 disabled:opacity-50">
            <Download className="h-3.5 w-3.5" /> {t("reports.exportReport")}
          </button>
        </div>
      </div>

      {loading || !data ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#275C3F]" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="report-resolution-rate" icon={ShieldCheck} label={t("reports.resolutionRate")} value={`${data.resolution_rate}%`} tone="#275C3F" />
            <Stat testid="report-avg-resolution" icon={Clock} label={t("reports.avgResolution")} value={`${data.avg_resolution_hours} ${t("reports.hours")}`} tone="#2E7599" />
            <Stat testid="report-auto-generated" icon={Zap} label={t("reports.autoGenerated")} value={data.auto_generated} tone="#D98014" />
            <Stat testid="report-completed" icon={CheckCircle2} label={t("reports.completedItems")} value={`${data.compliance_completed}/${data.compliance_total}`} tone="#6D9C55" />
          </div>

          <div className="grid lg:grid-cols-3 gap-6">
            <div className="bg-white border border-[#DCE3DD] rounded-sm p-5 lg:col-span-2">
              <h2 className="font-display text-lg font-semibold mb-4">{t("reports.incidentsOverTime")}</h2>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={288}>
                  <AreaChart data={data.timeseries} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                      <linearGradient id="incGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#275C3F" stopOpacity={0.25} />
                        <stop offset="100%" stopColor="#275C3F" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#EAF0EA" />
                    <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#4B5950" }} tickFormatter={(d) => d.slice(5)} interval="preserveStartEnd" minTickGap={24} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#4B5950" }} />
                    <Tooltip />
                    <Area type="monotone" dataKey="count" stroke="#275C3F" strokeWidth={2} fill="url(#incGrad)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="bg-white border border-[#DCE3DD] rounded-sm p-5">
              <h2 className="font-display text-lg font-semibold mb-4">{t("reports.severityDist")}</h2>
              {data.severity.length === 0 ? (
                <p className="text-sm text-[#4B5950] py-16 text-center">{t("reports.noData")}</p>
              ) : (
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={288}>
                    <PieChart>
                      <Pie data={data.severity} dataKey="count" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={2}>
                        {data.severity.map((s) => <Cell key={s.name} fill={SEV_COLORS[s.name] || "#4B5950"} />)}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex flex-wrap justify-center gap-3 -mt-4">
                    {data.severity.map((s) => (
                      <span key={s.name} className="flex items-center gap-1.5 text-xs text-[#4B5950]">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: SEV_COLORS[s.name] || "#4B5950" }} />
                        {t(`severity.${s.name}`)} ({s.count})
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat testid="report-total" icon={BarChart3} label={t("reports.totalIncidents")} value={data.total_incidents} tone="#121A14" />
            <Stat testid="report-open" icon={Zap} label={t("reports.openIncidents")} value={data.open_incidents} tone="#B84A22" />
          </div>
        </>
      )}
    </div>
  );
}
